import React, { useEffect, useRef, useState } from 'react';
import { AccessibilityWidget } from './components/AccessibilityWidget';
import { MobileMenu } from './components/MobileMenu';
import { useAccessibilityStore } from './store/accessibilityStore';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ServicesSection } from './components/ServicesSection';
import { AboutSection } from './components/AboutSection';
import { ContactSection } from './components/ContactSection';

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const store = useAccessibilityStore();
  const cursorRef = useRef<HTMLDivElement | null>(null);

  // Handle accessibility settings
  useEffect(() => {
    const root = document.documentElement;
    const settings = [
      ['--font-size', `${store.fontSize}px`],
      ['--line-spacing', store.lineSpacing.toString()],
      ['--text-color', store.textColor],
      ['--background-color', store.backgroundColor],
      ['--title-color', store.titleColor]
    ];
    
    settings.forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }, [store.fontSize, store.lineSpacing, store.textColor, store.backgroundColor, store.titleColor]);

  // Handle cursor settings
  useEffect(() => {
    if (!cursorRef.current) {
      const cursor = document.createElement('div');
      cursor.className = 'custom-cursor';
      document.body.appendChild(cursor);
      cursorRef.current = cursor;
    }

    const cursor = cursorRef.current;
    cursor.style.backgroundColor = store.cursorColor;

    const handleMouseMove = (e: MouseEvent) => {
      if (cursor) {
        cursor.style.setProperty('left', `${e.clientX}px`);
        cursor.style.setProperty('top', `${e.clientY}px`);
      }
    };

    if (store.bigCursor) {
      document.body.classList.add('big-cursor');
      document.addEventListener('mousemove', handleMouseMove);
    } else {
      document.body.classList.remove('big-cursor');
      document.removeEventListener('mousemove', handleMouseMove);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [store.bigCursor, store.cursorColor]);

  // Cleanup cursor on unmount
  useEffect(() => {
    return () => cursorRef.current?.remove();
  }, []);

  const accessibilityClasses = [
    store.highlightLinks && 'highlight-links',
    store.highlightTitles && 'highlight-titles',
    store.readableFont && 'readable-font',
    store.blockAnimations && 'block-animations',
    store.readingMask && 'reading-mask',
    store.reverseContrast && 'reverse-contrast',
    store.brightContrast && 'bright-contrast',
    store.colorlessDisplay && 'colorless',
    store.darkContrast && 'dark-contrast',
    store.highSaturation && 'high-saturation',
    store.readingMode && 'reading-mode',
    store.readingAid && 'reading-aid',
    store.keyboardNavigation && 'keyboard-navigation'
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={`min-h-screen bg-white relative ${accessibilityClasses}`} 
      data-show-descriptions={store.showImageDescriptions}
    >
      <AccessibilityWidget />
      <MobileMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      <main id="main-content">
        <AboutSection />
        <ServicesSection />
        <ContactSection />
      </main>

      <Footer />
    </div>
  );
}

export default App;